import { useEffect, useRef, useState } from "react"
import { Outlet, useLocation } from "react-router-dom"
import {
  LandPlot,
  ArrowLeftRight,
  PlusCircle,
  Menu,
  X,
  Activity,
  Upload,
  Pickaxe,
  FileSignature,
} from "lucide-react"
import { useClientTheme } from "@/app/components/ThemeContext"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { SidebarNav, type NavItem } from "@/app/components/SidebarNav"
import { GridMark } from "@/app/components/GridMark"
import { UserMenu } from "@/app/components/UserMenu"
import { hasCapability } from "@/app/lib/capabilities"

// Read-only nav — visible to every role. Master Inventory is the app home.
// Township-Range is intentionally hidden from nav (route still exists); the STR
// summary popout now covers that need from the inventory itself.
const readNavItems: NavItem[] = [
  {
    path: "/inventory",
    label: "Master Inventory",
    icon: <LandPlot size={15} strokeWidth={1.75} />,
    capability: "tracker",
  },
  {
    path: "/units",
    label: "Units & Wells",
    icon: <Pickaxe size={15} strokeWidth={1.75} />,
    capability: "wells",
  },
  {
    path: "/transactions",
    label: "Transaction History",
    icon: <ArrowLeftRight size={15} strokeWidth={1.75} />,
    capability: "tracker",
  },
  {
    path: "/leases",
    label: "Leases",
    icon: <FileSignature size={15} strokeWidth={1.75} />,
    capability: "leases",
  },

  // { path: "/orri", label: "ORRI", icon: <Percent size={15} strokeWidth={1.75} /> },
]

// Records nav — owner/admin (anyone who can write). Logging transactions.
const recordNavItems: NavItem[] = [
  {
    path: "/recent-updates",
    label: "Recent Updates",
    icon: <Activity size={15} strokeWidth={1.75} />,
    capability: "tracker",
  },
]

// MANAGE nav — admin role only. Triggers a domain import (Google Sheet / CSV).
// Each entry MUST have a real route in routes.tsx.
const manageNavItems: NavItem[] = [
  {
    path: "/new-transaction",
    label: "New Transactions",
    icon: <PlusCircle size={15} strokeWidth={1.75} />,
    group: "Manage",
    capability: "tracker",
  },
  {
    path: "/import",
    label: "Import",
    icon: <Upload size={15} strokeWidth={1.75} />,
    group: "Manage",
    capability: "tracker",
  },
]

function filterByCapability(items: NavItem[]): NavItem[] {
  return items.filter((item) => !item.capability || hasCapability(item.capability))
}

// Dark-mode switch (Light Forward design): 36×20 track, 16px thumb, 150ms ease.
function DarkModeToggle() {
  const { theme, mode, toggleMode } = useClientTheme()
  const on = mode === "dark"
  return (
    <button
      type="button"
      onClick={toggleMode}
      role="switch"
      aria-checked={on}
      aria-label="Dark mode"
      className="flex items-center gap-2"
      style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
    >
      <span
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "12px",
          fontWeight: 500,
          color: theme.textMuted,
          whiteSpace: "nowrap",
        }}
      >
        Dark mode
      </span>
      <span
        style={{
          position: "relative",
          display: "inline-block",
          width: "36px",
          height: "20px",
          borderRadius: "10px",
          backgroundColor: on ? "#2E7D4F" : theme.bgSurface2,
          border: `1px solid ${on ? "#2E7D4F" : theme.borderColor}`,
          transition: "background-color 150ms ease, border-color 150ms ease",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: "1px",
            left: on ? "17px" : "1px",
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
            transition: "left 150ms ease",
          }}
        />
      </span>
    </button>
  )
}

// "Powered by" lockup pinned to the sidebar bottom (desktop aside + mobile drawer).
function PoweredByLockup() {
  const { theme } = useClientTheme()
  return (
    <a
      href="https://landtrackapp.com"
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center"
      style={{
        marginTop: "auto",
        padding: "14px 12px 16px",
        borderTop: `1px solid ${theme.borderColor}`,
        gap: "8px",
        textDecoration: "none",
      }}
      title="Powered by LandTrack"
    >
      <span
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "9px",
          color: theme.textDim,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        Powered by
      </span>
      <GridMark size={18} cellFill={theme.borderColor} />
      {/* LandTrack wordmark — brand green kept fixed across client themes */}
      <span
        style={{
          fontFamily: "var(--font-brand)",
          fontSize: "14px",
          fontWeight: 600,
          letterSpacing: "-0.01em",
          lineHeight: 1,
        }}
      >
        <span style={{ color: theme.textMuted }}>Land</span>
        <span style={{ color: "#3B9B63" }}>Track</span>
      </span>
    </a>
  )
}

export function Shell() {
  const { theme, hasDarkVariant } = useClientTheme()
  const location = useLocation()
  const isMobile = useIsMobile()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Soft ease-in on client-side navigations only — the initial page load skips
  // the animation so first paint (and LCP) is never delayed.
  const isFirstRender = useRef(true)
  useEffect(() => {
    isFirstRender.current = false
  }, [])

  const { user, canWrite, isStaff } = useCurrentUser()
  const clientName = user?.client?.name ?? ""

  const navItems = [
    ...filterByCapability(readNavItems),
    ...(canWrite ? filterByCapability(recordNavItems) : []),
    ...(isStaff ? filterByCapability(manageNavItems) : []),
  ]

  // Close the mobile drawer whenever the route changes or we leave mobile.
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false)
  }, [isMobile])

  return (
    <div
      className="flex flex-col h-screen overflow-hidden"
      style={
        {
          backgroundColor: theme.bgPage,
          fontFamily: "var(--font-ui)",
          colorScheme: theme.colorScheme,
          // Native controls (checkboxes) pick this up via accent-color.
          "--brand-primary": theme.brandPrimary,
        } as React.CSSProperties
      }
    >
      {/* ── Header (full width, top) ─────────────────────────────────────── */}
      <header
        className="flex-shrink-0 flex items-center justify-between"
        style={{
          height: "60px",
          paddingLeft: isMobile ? "12px" : "24px",
          paddingRight: isMobile ? "12px" : "24px",
          backgroundColor: theme.bgHeader,
          borderBottom: `1px solid ${theme.borderColor}`,
          boxShadow: "0 1px 0 0 rgba(0,0,0,0.04)",
        }}
      >
        {/* Brand + breadcrumb */}
        <div className="flex items-center gap-2">
          {isMobile && (
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation"
              className="flex items-center justify-center rounded-lg"
              style={{
                width: "36px",
                height: "36px",
                marginRight: "2px",
                color: theme.textPrimary,
                backgroundColor: "transparent",
              }}
            >
              <Menu size={20} />
            </button>
          )}
          <span
            style={{
              fontFamily: "var(--font-brand)",
              fontSize: isMobile ? "15px" : "18px",
              color: theme.textPrimary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: isMobile ? "62vw" : "none",
            }}
          >
            <span style={{ fontWeight: 600 }}>{clientName}</span>
            {!isMobile && (
              <span style={{ fontWeight: 500, color: theme.textMuted }}>
                {clientName ? " " : ""}Tracker
              </span>
            )}
          </span>
          {/* <span style={{ color: theme.textDim, fontSize: "13px" }}>/</span>
          <span style={{ fontFamily: "var(--font-ui)", fontSize: "13px", fontWeight: 600, color: theme.textPrimary }}>
            {pageLabel}
          </span> */}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-3">
          {/* Dark mode — only for themes with a dark counterpart */}
          {hasDarkVariant && <DarkModeToggle />}

          {/* Bell
          <button
            className="flex items-center justify-center w-8 h-8 rounded-lg border relative"
            style={{ borderColor: theme.borderColor, backgroundColor: theme.bgSurface2, color: theme.textMuted }}
          >
            <Bell size={14} />
            <span
              className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: theme.brandAccent }}
            />
          </button>
          */}

          {/* Portfolio total 
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-xl border"
            style={{ borderColor: `${theme.brandPrimary}30`, backgroundColor: `${theme.brandPrimary}08` }}
          >
            <TrendingUp size={13} style={{ color: theme.brandPrimary }} />
            <div>
              <div style={{ fontFamily: "var(--font-ui)", fontSize: "10px", color: theme.textMuted, lineHeight: 1 }}>
                Portfolio Total
              </div>
              <div style={{ fontFamily: "var(--font-data)", fontSize: "14px", fontWeight: 600, color: theme.brandPrimary, lineHeight: 1.3 }}>
                Est. Value
              </div>
            </div>
          </div>
          */}

          {/* Divider — hidden on mobile to save header space */}
          {!isMobile && (
            <div style={{ width: "1px", height: "24px", backgroundColor: theme.borderColor }} />
          )}

          {/* Signed-in user — dropdown menu (collapses to avatar on mobile) */}
          <UserMenu />
        </div>
      </header>

      {/* ── Body row (sidebar + content) ─────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ── Desktop sidebar ────────────────────────────────────────────── */}
        {!isMobile && (
          <aside
            className="flex-shrink-0 flex flex-col"
            style={{
              width: "210px",
              backgroundColor: theme.bgSidebar,
              borderRight: `1px solid ${theme.borderColor}`,
              boxShadow: "1px 0 0 0 rgba(0,0,0,0.04)",
            }}
          >
            <SidebarNav items={navItems} />
            <PoweredByLockup />
          </aside>
        )}

        {/* ── Mobile drawer (overlay + slide-in panel) ───────────────────── */}
        {isMobile && drawerOpen && (
          <div className="fixed inset-0 z-50 flex">
            {/* Backdrop */}
            <div
              onClick={() => setDrawerOpen(false)}
              className="absolute inset-0"
              style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
            />
            {/* Panel */}
            <aside
              className="relative flex flex-col"
              style={{
                width: "min(280px, 82vw)",
                backgroundColor: theme.bgSidebar,
                borderRight: `1px solid ${theme.borderColor}`,
                boxShadow: "4px 0 24px rgba(0,0,0,0.18)",
              }}
            >
              <div
                className="flex items-center justify-between px-4"
                style={{ height: "60px", borderBottom: `1px solid ${theme.borderColor}` }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-brand)",
                    fontSize: "16px",
                    fontWeight: 600,
                    color: theme.textPrimary,
                  }}
                >
                  {clientName}
                </span>
                <button
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Close navigation"
                  className="flex items-center justify-center rounded-lg"
                  style={{
                    width: "36px",
                    height: "36px",
                    color: theme.textMuted,
                    backgroundColor: "transparent",
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <SidebarNav items={navItems} onNavigate={() => setDrawerOpen(false)} />
              <PoweredByLockup />
            </aside>
          </div>
        )}

        {/* ── Page content ───────────────────────────────────────────────── */}
        <main className="flex-1 overflow-auto" style={{ backgroundColor: theme.bgPage }}>
          {/* Keyed on the path so each navigation remounts and replays the fade.
              h-full gives the wrapper a definite height so viewport-fit pages
              (Transactions) can flex to it; normal pages simply overflow it and
              <main> scrolls exactly as before. */}
          <div
            key={location.pathname}
            className={isFirstRender.current ? "h-full" : "page-enter h-full"}
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
